/**
 * useVCheckLive — WebSocket-Hook für Multi-Rep-Live-Status.
 * =========================================================
 * Iter89z-wiz3 (2026-02-26).
 *
 * - Baut genau 1 WebSocket pro Projekt auf
 * - Reconnect bei Verbindungsabbruch (exponential backoff, max 30s)
 * - Liefert: `{ presence: [users], online: bool, lastRemoteChange }`
 * - `lastRemoteChange` wird vom Wizard beobachtet, um nach fremden
 *   Edits das Formular neu zu laden
 */
import { useEffect, useRef, useState } from "react";


function buildWsUrl(projectId, token) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const wsBase = base.replace(/^http/, "ws");
  return `${wsBase}/api/ws/vcheck/${projectId}?token=${encodeURIComponent(token || "")}`;
}


export function useVCheckLive(projectId) {
  const [presence, setPresence] = useState([]);
  const [online, setOnline] = useState(false);
  const [lastRemoteChange, setLastRemoteChange] = useState(null);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;
    let closed = false;

    const connect = () => {
      if (closed) return;
      let token = "";
      try { token = localStorage.getItem("novatus_token") || ""; } catch {}
      const url = buildWsUrl(projectId, token);
      let ws;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        schedule();
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        setOnline(true);
        retryRef.current = 0;
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "presence") {
            setPresence(msg.users || []);
          } else if (msg.type === "field_change") {
            setLastRemoteChange({
              key: msg.key, value: msg.value, by: msg.by,
              by_name: msg.by_name, ts: msg.ts,
            });
          }
          // ping-Nachrichten werden ignoriert
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setOnline(false);
        if (!closed) schedule();
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };

    const schedule = () => {
      if (closed) return;
      retryRef.current = Math.min(retryRef.current + 1, 6);
      const delay = Math.min(1000 * 2 ** retryRef.current, 30000);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(connect, delay);
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(timerRef.current);
      try { wsRef.current?.close(); } catch {}
    };
  }, [projectId]);

  return { presence, online, lastRemoteChange };
}
